import React, { useRef, useState, useEffect } from "react";
import { Task } from "../utils/taskGenerator";
import { useTasks } from "../store/useTasks";

interface Props {
  tasks: Task[];
}

type SortKey = "title" | "priority" | "dueDate";

const priorityOrder: Record<string, number> = {
  Critical: 1,
  High: 2,
  Medium: 3,
  Low: 4,
};

const ROW_HEIGHT = 50; // px height per row
const BUFFER = 5;      // extra rows above/below viewport

const ListView: React.FC<Props> = ({ tasks }) => {
  const { updateTask } = useTasks();
  const [sortKey, setSortKey] = useState<SortKey>("title");
  const [sortAsc, setSortAsc] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    if (containerRef.current) {
      setVisibleCount(Math.ceil(containerRef.current.clientHeight / ROW_HEIGHT));
    }
  }, []);

  const handleScroll = () => {
    if (containerRef.current) {
      setScrollTop(containerRef.current.scrollTop);
    }
  };

  // Sorting logic
  const sortedTasks = [...tasks].sort((a, b) => {
    if (sortKey === "title") {
      return sortAsc
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    }
    if (sortKey === "priority") {
      return sortAsc
        ? priorityOrder[a.priority] - priorityOrder[b.priority]
        : priorityOrder[b.priority] - priorityOrder[a.priority];
    }
    if (sortKey === "dueDate") {
      return sortAsc
        ? new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        : new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
    }
    return 0;
  });

  // Virtual scrolling calculations
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER);
  const endIndex = Math.min(
    sortedTasks.length,
    startIndex + visibleCount + BUFFER * 2
  );
  const visibleTasks = sortedTasks.slice(startIndex, endIndex);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="overflow-auto max-h-[80vh] border rounded bg-white"
    >
      {/* Responsive wrapper for table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs sm:text-sm relative">
          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th
                className="px-2 sm:px-4 py-2 cursor-pointer"
                onClick={() => handleSort("title")}
              >
                Title {sortKey === "title" && (sortAsc ? "▲" : "▼")}
              </th>
              <th
                className="px-2 sm:px-4 py-2 cursor-pointer"
                onClick={() => handleSort("priority")}
              >
                Priority {sortKey === "priority" && (sortAsc ? "▲" : "▼")}
              </th>
              <th
                className="px-2 sm:px-4 py-2 cursor-pointer"
                onClick={() => handleSort("dueDate")}
              >
                Due Date {sortKey === "dueDate" && (sortAsc ? "▲" : "▼")}
              </th>
              <th className="px-2 sm:px-4 py-2">Status</th>
              <th className="px-2 sm:px-4 py-2">Assignee</th>
            </tr>
          </thead>
        </table>
      </div>

      {/* Empty state */}
      {sortedTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-gray-500 italic">
          <span>No tasks match your filters</span>
          <span className="text-xs mt-1">Try clearing filters to see all tasks</span>
        </div>
      ) : (
        <div style={{ height: sortedTasks.length * ROW_HEIGHT }}>
          <div style={{ transform: `translateY(${startIndex * ROW_HEIGHT}px)` }}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs sm:text-sm">
                <tbody>
                  {visibleTasks.map((task) => (
                    <tr key={task.id} className="border-t h-[50px]">
                      <td className="px-2 sm:px-4 py-2">{task.title}</td>
                      <td className="px-2 sm:px-4 py-2">{task.priority}</td>
                      <td className="px-2 sm:px-4 py-2">
                        {task.dueDate === new Date().toISOString().split("T")[0]
                          ? "Due Today"
                          : task.dueDate}
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <select
                          value={task.status}
                          onChange={(e) =>
                            updateTask(task.id, { status: e.target.value as any })
                          }
                          className="border rounded px-2 py-1 text-xs sm:text-sm"
                        >
                          <option>To Do</option>
                          <option>In Progress</option>
                          <option>In Review</option>
                          <option>Done</option>
                        </select>
                      </td>
                      <td className="px-2 sm:px-4 py-2">{task.assignee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListView;
